/**
 * The open world's chunked map data (docs/archive/OPEN_WORLD.md §4.1): the contract the chunked map
 * (lane A, `map.ts` accessors and the store), the region generator (lane B) and the client's
 * streaming (lane C) build against.
 *
 * A region is a square of `REGION_CHUNKS` × `REGION_CHUNKS` data chunks of `CHUNK` × `CHUNK` one-metre
 * tiles. A chunk is a pure function of (sketch, stamps, ci, cj): no world seed, no access order.
 * Layers are row-major inside the chunk (index `lj * CHUNK + li`). Levels are centimetres over the
 * chunk's own base, so peaks over 327 m fit an Int16.
 */

/** Tiles along a data chunk's side (64 m). Render chunks stay 32 m. */
export const CHUNK = 64;
/** Data chunks along the region's side (its bounding square): 54 × 64 = 3,456 m (Yanyu, docs/archive/HUB.md §5). */
export const REGION_CHUNKS = 54;
/** The region's side in tiles (metres). */
export const REGION_SIZE = CHUNK * REGION_CHUNKS;

/** One generated data chunk. Optional layers are absent where the chunk has no authored content. */
export interface ChunkData {
  readonly ci: number;
  readonly cj: number;
  /** `Tile` ids. */
  readonly tiles: Uint8Array;
  readonly variant: Uint8Array;
  /** The chunk's level base in centimetres; a tile's level is `base + level[n]`. */
  readonly base: number;
  /** Centimetres over `base`. */
  readonly level: Int16Array;
  /** Bit 1: a wall across the tile's east side; bit 2: across its south side (as `GameMap.edge`). */
  readonly edge: Uint8Array;
  /** `Surface` ids. */
  readonly surface: Uint8Array;
  readonly owner?: Uint16Array;
  readonly flight?: Uint16Array;
  readonly safe?: Uint8Array;
  readonly made?: Uint8Array;
  readonly deep?: Uint8Array;
  /**
   * The water's surface in centimetres over `base`, on the chunk's `Tile.Water` tiles (the sea 0,
   * a lake its own level, a river its water over the bed); `level` there is the bed. Absent in a
   * chunk with no water. Read through `waterSurfaceAt` (map.ts).
   */
  readonly water?: Int16Array;
}

/** Builds a chunk. Must be pure: the same (ci, cj) gives the same bytes, in any order. */
export type ChunkGenerator = (ci: number, cj: number) => ChunkData;

/**
 * Where a chunked `GameMap` gets its chunks: a flat `REGION_CHUNKS`² table, built on first touch.
 * The client may evict chunks (an LRU); a rebuilt chunk has the same bytes.
 */
export interface ChunkStore {
  /** Chunks along the region's side. */
  readonly chunks: number;
  /** The chunk, generating it if needed. */
  ensure(ci: number, cj: number): ChunkData;
  /** The chunk if it is built, without generating it. */
  peek(ci: number, cj: number): ChunkData | undefined;
  /** Drop a built chunk (the client's LRU). */
  evict?(ci: number, cj: number): void;
  /** A chunk the World has written to (`setTileAt`): it must never be evicted. */
  dirty?(ci: number, cj: number): void;
  /**
   * Put a chunk built elsewhere (the client's chunk worker) into its empty slot. It must hold the
   * bytes `ensure` would generate (generators are pure). A slot already built is kept as it is.
   * Returns whether the chunk went in.
   */
  install?(chunk: ChunkData): boolean;
}

/** Index of tile (i, j) inside its chunk. */
export const chunkIndex = (i: number, j: number): number => (j & (CHUNK - 1)) * CHUNK + (i & (CHUNK - 1));
